import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import { useRowSelection } from '../../components/requests/BulkSelection';
import { selectColumn } from '../../components/requests/RequestActions';
import { listRequestCandidates, createCandidateRequest } from '../../api/candidateRequests';
import { REQUEST_TYPES } from '../../utils/candidateRequestTypes';
import { pushToast } from '../../components/ui/toastBus';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

const dash = (v) => v || <span className="text-gray-400">—</span>;

export default function CandidateRequestPage({ type }) {
  const meta = REQUEST_TYPES[type];
  const queryClient = useQueryClient();
  const filters = useCandidateFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const candidatesQuery = useQuery({
    queryKey: [`${type}-request-candidates`, filters.params, search],
    queryFn: () => listRequestCandidates(type, { ...filters.params, search: search || undefined }),
  });
  const candidates = useMemo(() => (candidatesQuery.data || []).map((c, i) => ({ ...c, no: i + 1 })), [candidatesQuery.data]);

  const selection = useRowSelection(candidates, (c) => c.id, (c) => !c.has_pending_request);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const bulkMutation = useMutation({
    // One after another, like the approval bulk actions: a failure on one doesn't stop the rest.
    mutationFn: async (ids) => {
      let failed = 0;
      for (const id of ids) {
        try {
          await createCandidateRequest(type, { candidate_id: id });
        } catch {
          failed += 1;
        }
      }
      return { ok: ids.length - failed, failed };
    },
    onSuccess: ({ ok, failed }) => {
      pushToast({
        type: failed ? 'error' : 'success',
        message: failed ? `${ok} ${meta.label.toLowerCase()} request(s) submitted, ${failed} failed.` : `${ok} ${meta.label.toLowerCase()} request(s) submitted.`,
      });
      selection.clear();
      setBulkConfirm(false);
      queryClient.invalidateQueries({ queryKey: [`${type}-request-candidates`] });
      queryClient.invalidateQueries({ queryKey: [`${type}-requests`] });
    },
  });

  const requestMutation = useMutation({
    mutationFn: (candidate) => createCandidateRequest(type, { candidate_id: candidate.id }),
    onSuccess: () => {
      pushToast({ type: 'success', message: `${meta.label} request submitted.` });
      queryClient.invalidateQueries({ queryKey: [`${type}-request-candidates`] });
      queryClient.invalidateQueries({ queryKey: [`${type}-requests`] });
      setTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.errors?.candidate_id?.[0] || err.response?.data?.message || `Could not submit ${meta.label.toLowerCase()} request.` });
      setTarget(null);
    },
  });

  const columns = [
    selectColumn(selection, (r) => r.id, (r) => !r.has_pending_request),
    { key: 'no', label: 'No' },
    { key: 'full_name', label: 'Name' },
    { key: 'vehicle_type', label: 'Vehicle Type', render: (row) => <span className="capitalize">{dash(row.vehicle_type)}</span> },
    { key: 'passport_no', label: 'Passport No', render: (row) => dash(row.passport_no) },
    { key: 'nationality', label: 'Nationality', render: (row) => dash(row.nationality) },
    { key: 'location', label: 'Location', render: (row) => <span className="capitalize">{dash(row.location)}</span> },
    { key: 'current_step', label: 'Current Workflow Stage', render: (row) => dash(row.current_step) },
    { key: 'rms_no', label: 'RMS No', render: (row) => dash(row.rms_no) },
    { key: 'jde_no', label: 'JDE No', render: (row) => dash(row.jde_no) },
    { key: 'stamping_date', label: 'Stamping Date', render: (row) => dash(row.stamping_date) },
    { key: 'source', label: 'Source', render: (row) => dash(row.source) },
    { key: 'contact_no', label: 'UAE Contact No', render: (row) => dash(row.contact_no) },
    { key: 'home_country_contact_no', label: 'Home Country Contact No', render: (row) => dash(row.home_country_contact_no) },
    {
      key: 'actions',
      label: '',
      render: (row) =>
        row.has_pending_request ? (
          <Badge tone="amber">Requested</Badge>
        ) : (
          <Button onClick={() => setTarget(row)}>Request</Button>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={`${meta.label} Requests`}
        description={`Find candidates by vehicle type, location, license type, workflow, step or name, and raise a ${meta.label.toLowerCase()} request for them.`}
      />

      <CandidateFilterBar {...filters.barProps}>
        <Input placeholder="Search name, reference, passport, contact…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </CandidateFilterBar>

      {(
        <>
          <SearchSummary search={search} total={candidates.length} rows={candidates} plainCount={`${candidates.length} shown`} />
          {selection.selectableCount > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Select all ({selection.selectableCount})
              </label>
              {selection.selectedIds.length > 0 && (
                <>
                  <span className="text-sm text-gray-500">{selection.selectedIds.length} selected</span>
                  <Button className="ml-auto" onClick={() => setBulkConfirm(true)}>
                    Request selected
                  </Button>
                </>
              )}
            </div>
          )}
          <Table columns={withMatchColumn(columns, search)} data={candidates} isLoading={candidatesQuery.isLoading} emptyMessage="No candidates match this filter." />
        </>
      )}

      <ConfirmDialog
        open={bulkConfirm}
        onClose={() => setBulkConfirm(false)}
        onConfirm={() => bulkMutation.mutate(selection.selectedIds)}
        title={`Request ${meta.label.toLowerCase()}`}
        message={`Raise a ${meta.label.toLowerCase()} request for ${selection.selectedIds.length} selected candidate(s)? They will go to ${meta.label} Request approvals for review.`}
        confirmLabel="Request"
        variant="primary"
        loading={bulkMutation.isPending}
      />

      <ConfirmDialog
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirm={() => requestMutation.mutate(target)}
        title={`Request ${meta.label.toLowerCase()}`}
        message={`Raise a ${meta.label.toLowerCase()} request for ${target?.full_name} (${target?.reference_no})? It will go to ${meta.label} Request approvals for review.`}
        confirmLabel="Request"
        variant="primary"
        loading={requestMutation.isPending}
      />
    </div>
  );
}
